import { Title } from '@solidjs/meta'
import { type Component, createEffect, createResource, Show } from 'solid-js'
import { z } from 'zod'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Form from '~/components/Form'
import Icon from '~/components/Icon'
import Input from '~/components/Input'
import Select from '~/components/Select'
import Switch from '~/components/Switch'
import { useToast } from '~/components/useToast'
import { useForm } from '~/hooks/useForm'
import { usePermissions } from '~/hooks/usePermissions'
import { useNavigate } from '~/router'
import api from '~/services/api'
import { oauthService } from '~/services/oauth.service'
import { useSettings } from '~/stores/settings'
import { resolveError } from '~/utils/error'

const oauthSchema = z.object({
  googleEnabled: z.boolean().default(false),
  googleClientId: z.string().trim().default(''),
  googleClientSecret: z.string().trim().default(''),
  githubEnabled: z.boolean().default(false),
  githubClientId: z.string().trim().default(''),
  githubClientSecret: z.string().trim().default('')
})

type OauthFormValuesType = z.infer<typeof oauthSchema>

const ConfiguracionPage: Component = () => {
  const { settings: uiSettings, updateTheme } = useSettings()
  const { isRoot } = usePermissions()
  const toast = useToast()
  const navigate = useNavigate()

  // ── OAuth: configuración global de la plataforma (solo root) ─────────────
  const oauthForm = useForm<OauthFormValuesType>({
    schema: oauthSchema,
    initialValues: () => ({
      googleEnabled: false,
      googleClientId: '',
      googleClientSecret: '',
      githubEnabled: false,
      githubClientId: '',
      githubClientSecret: ''
    }),
    onSubmit: async values => {
      const payload = [
        {
          provider: 'google' as const,
          enabled: Boolean(values.googleEnabled),
          clientId: String(values.googleClientId ?? '').trim(),
          clientSecret: String(values.googleClientSecret ?? '').trim() || undefined
        },
        {
          provider: 'github' as const,
          enabled: Boolean(values.githubEnabled),
          clientId: String(values.githubClientId ?? '').trim(),
          clientSecret: String(values.githubClientSecret ?? '').trim() || undefined
        }
      ]
      await oauthService.saveProviders(payload)
      toast.addToast({ title: 'Configuración de OAuth guardada', type: 'success' })
      await refetchOauth()
    },
    onError: err => toast.addToast({ title: resolveError(err), type: 'danger' })
  })

  const [oauthResource, { refetch: refetchOauth }] = createResource(async () => {
    const res = await oauthService.getProviders()
    const google = res.providers.find(p => p.provider === 'google')
    const github = res.providers.find(p => p.provider === 'github')
    oauthForm.setValues({
      googleEnabled: google?.enabled ?? false,
      googleClientId: google?.clientId ?? '',
      googleClientSecret: '',
      githubEnabled: github?.enabled ?? false,
      githubClientId: github?.clientId ?? '',
      githubClientSecret: ''
    })
    return res
  })

  // Mantener clientSecretSet para el placeholder sin almacenarlo en el formulario
  const oauthProviders = () => oauthResource()?.providers ?? []
  const googleSecretSet = () => oauthProviders().find(p => p.provider === 'google')?.clientSecretSet ?? false
  const githubSecretSet = () => oauthProviders().find(p => p.provider === 'github')?.clientSecretSet ?? false
  // Sincroniza si el recurso llega después del primer render sin perder el estado del form
  createEffect(() => {
    const res = oauthResource()
    if (!res) return
    // ya hidratado en el fetcher; efecto por si se refetchea
  })

  return (
    <Container>
      <Title>Configuración | Florez Jaramillo</Title>

      <h1 class="text-2xl font-bold text-foreground">Configuración</h1>
      <p class="text-sm text-foreground-subtle mb-4">
        Ajustes de la plataforma, no de una empresa concreta: la configuración de cada empresa (formatos, PDF, datos de pago, catálogos) vive en Empresas › Configurar empresa.
      </p>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <Card.Title>
            <div class="flex items-center">
              <Icon icon="material-symbols-light:palette-outline" size={20} class="mr-2" />
              <h2 class="text-xl font-semibold">Apariencia</h2>
            </div>
          </Card.Title>
          <Card.Body>
            <div class="space-y-4">
              {/* Sin `name`: el tema es de este dispositivo, no de la plataforma. */}
              <Select
                label="Tema"
                value={uiSettings.theme}
                onChange={v => updateTheme(v === 'dark' ? 'dark' : 'light')}
                options={[
                  { value: 'light', title: 'Claro' },
                  { value: 'dark', title: 'Oscuro' }
                ]}
              />
              <p class="text-xs text-foreground-subtle">El tema se aplica al instante y se guarda en este dispositivo, no en la plataforma.</p>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Title>
            <div class="flex items-center">
              <Icon icon="material-symbols-light:manage-accounts-outline" size={20} class="mr-2" />
              <h2 class="text-xl font-semibold">Usuarios y permisos</h2>
            </div>
          </Card.Title>
          <Card.Body>
            <p class="text-sm text-foreground-subtle mb-4">La gestión de usuarios, roles y permisos vive en sus propias pantallas.</p>
            <div class="flex gap-2">
              <Button type="button" variant="soft" color="default" onClick={() => navigate('/admin/usuarios')} prependIcon="material-symbols-light:badge-outline">
                Usuarios
              </Button>
              <Button type="button" variant="soft" color="default" onClick={() => navigate('/admin/roles')} prependIcon="material-symbols-light:shield-outline">
                Roles y permisos
              </Button>
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Title>
            <div class="flex items-center">
              <Icon icon="material-symbols-light:mail-outline" size={20} class="mr-2" />
              <h2 class="text-xl font-semibold">Correo e IA</h2>
            </div>
          </Card.Title>
          <Card.Body>
            <p class="text-sm text-foreground-subtle mb-4">Servidores de correo y conexiones de IA de la plataforma.</p>
            <div class="flex gap-2">
              <Button type="button" variant="soft" color="default" onClick={() => navigate('/admin/configuracion/correo')} prependIcon="material-symbols-light:mail-outline">
                Correo
              </Button>
              <Button type="button" variant="soft" color="default" onClick={() => navigate('/admin/configuracion/ia')} prependIcon="material-symbols-light:smart-toy-outline">
                IA
              </Button>
              <Button type="button" variant="soft" color="default" onClick={() => navigate('/admin/configuracion/pagos')} prependIcon="material-symbols-light:credit-card-outline">
                Pagos
              </Button>
              <Button type="button" variant="soft" color="default" onClick={() => navigate('/admin/configuracion/auditoria')} prependIcon="material-symbols-light:database-outline">
                Auditoría
              </Button>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Show when={isRoot()}>
        <Card class="mt-6">
          <Card.Title>
            <div class="flex items-center">
              <Icon icon="material-symbols-light:vpn-key-outline" size={20} class="mr-2" />
              <h2 class="text-xl font-semibold">Inicio de sesión (OAuth)</h2>
            </div>
          </Card.Title>
          <Card.Body>
            <p class="text-sm text-foreground-subtle mb-4">
              Configuración global de la plataforma: los proveedores activos aparecen como botones en la pantalla de inicio de sesión. El secret se guarda solo en el backend y jamás se muestra; dejar
              el campo vacío conserva el actual.
            </p>

            <Form controller={oauthForm} id="oauth-form">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div class="rounded-lg border border-border p-4 space-y-4">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <Icon icon="logos:google-icon" size={22} />
                      <h3 class="text-sm font-semibold text-foreground">Google</h3>
                    </div>
                    <Switch name="googleEnabled" label="Activo" checked={oauthForm.values().googleEnabled as boolean} onChange={e => oauthForm.setValue('googleEnabled', e.currentTarget.checked)} />
                  </div>
                  <Input name="googleClientId" label="Client ID" placeholder="xxxx.apps.googleusercontent.com" />
                  <Input
                    name="googleClientSecret"
                    label="Client Secret"
                    type="password"
                    placeholder={googleSecretSet() && !oauthForm.values().googleClientSecret ? '•••••••• (se conserva el actual)' : 'Client secret de Google Cloud'}
                  />
                </div>

                <div class="rounded-lg border border-border p-4 space-y-4">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                      <Icon icon="mdi:github" size={22} />
                      <h3 class="text-sm font-semibold text-foreground">GitHub</h3>
                    </div>
                    <Switch name="githubEnabled" label="Activo" checked={oauthForm.values().githubEnabled as boolean} onChange={e => oauthForm.setValue('githubEnabled', e.currentTarget.checked)} />
                  </div>
                  <Input name="githubClientId" label="Client ID" placeholder="OAuth App Client ID" />
                  <Input
                    name="githubClientSecret"
                    label="Client Secret"
                    type="password"
                    placeholder={githubSecretSet() && !oauthForm.values().githubClientSecret ? '•••••••• (se conserva el actual)' : 'OAuth App Client Secret'}
                  />
                  <p class="text-xs text-foreground-subtle">
                    En la OAuth App de GitHub, la <em>Authorization callback URL</em> debe ser{' '}
                    <code class="text-primary">{api.defaults.baseURL ?? 'http://localhost:3002/api'}/auth/oauth/github/callback</code>.
                  </p>
                </div>
              </div>
            </Form>

            <div class="flex justify-end mt-4">
              <Button type="submit" form="oauth-form" color="primary" disabled={oauthResource.loading || oauthForm.submitting()}>
                {oauthResource.loading ? 'Cargando...' : oauthForm.submitting() ? 'Guardando...' : 'Guardar OAuth'}
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Show>
    </Container>
  )
}

export default ConfiguracionPage
